import {
  Condition,
  DailyActivityLevel,
  DietaryGoals,
  EatOut,
  Ethnicity,
  FeelEnergized,
  GeneticSex,
  HealthHistoryQuestionnaireKey,
  HowOftenSick,
  PhysicalActivityType,
  PregnantLactating,
  StressImpactAppetite,
  SugaryDrinks,
  ToxinConcern
} from './health-history';
import { heightOptions } from './height-options';
import { waistCircumferenceOptions } from './waist-circumference-options';

export enum HealthHistoryQuestionType {
  Date = 'Date',
  Radio = 'Radio',
  Checkbox = 'Checkbox',
  Segment = 'Segment',
  Number = 'Number',
  String = 'String',
  Select = 'Select',
  MultiSelect = 'MultiSelect',
  Slider = 'Slider'
}

export interface HealthHistoryQuestionOption {
  label: string;
  labelValue1?: string | number;
  labelValue2?: string | number;
  description?: string;
  icon?: string;
  value: any;
  triggeredBy?: HealthHistoryQuestionnaireKey;
  triggeredByValues?: any[];
}

export interface HealthHistoryQuestion {
  key: HealthHistoryQuestionnaireKey;
  type: HealthHistoryQuestionType;
  label: string;
  placeholder?: string;
  options?: HealthHistoryQuestionOption[];
  lowValue?: number;
  lowValueLabel?: string;
  highValue?: number;
  highValueLabel?: string;
  triggeredBy?: HealthHistoryQuestionnaireKey;
  triggeredByValues?: any[];
}

export const birthday: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.Birthday,
  type: HealthHistoryQuestionType.Date,
  label: 'Health_History.Birthday.Label'
};

export const geneticSex: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.GeneticSex,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Genetic_Sex.Label',
  options: [
    {
      label: 'Health_History.Genetic_Sex.Male',
      value: GeneticSex.Male,
      icon: 'assets/icon/health-history/gender-male.svg'
    },
    {
      label: 'Health_History.Genetic_Sex.Female',
      value: GeneticSex.Female,
      icon: 'assets/icon/health-history/gender-female.svg'
    }
  ]
};

export const pregnantLactating: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.PregnantLactating,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Pregnant_Lactating.Label',
  options: [
    {
      label: 'Health_History.Pregnant_Lactating.Neither',
      value: PregnantLactating.Neither
    },
    {
      label: 'Health_History.Pregnant_Lactating.Pregnant',
      value: PregnantLactating.Pregnant
    },
    {
      label: 'Health_History.Pregnant_Lactating.Lactating',
      value: PregnantLactating.Lactating
    }
  ],
  triggeredBy: HealthHistoryQuestionnaireKey.GeneticSex,
  triggeredByValues: [GeneticSex.Female]
};

export const height: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.Height,
  type: HealthHistoryQuestionType.Select,
  label: 'Health_History.Height.Label',
  placeholder: 'Health_History.Height.Select_Height',
  options: heightOptions
};

export const weight: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.Weight,
  type: HealthHistoryQuestionType.Number,
  label: 'Health_History.Weight.Label',
  placeholder: 'Health_History.Weight.Enter_Weight'
};

export const waistCircumference: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.WaistCircumference,
  type: HealthHistoryQuestionType.Select,
  label: 'Health_History.Waist_Circumference.Label',
  placeholder: 'Health_History.Waist_Circumference.Select_Waist_Size',
  options: waistCircumferenceOptions,
  triggeredBy: HealthHistoryQuestionnaireKey.GeneticSex,
  triggeredByValues: [GeneticSex.Male, GeneticSex.Female]
};

export const dailyActivityLevel: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.DailyActivityLevel,
  type: HealthHistoryQuestionType.Select,
  label: 'Health_History.Daily_Activity_Level.Label',
  placeholder: 'Health_History.Daily_Activity_Level.Select_Activity_Level',
  options: [
    {
      label: 'Health_History.Daily_Activity_Level.Little_To_No_Activity',
      value: DailyActivityLevel.None
    },
    {
      label: 'Health_History.Daily_Activity_Level.Lightly_Active',
      value: DailyActivityLevel.Light
    },
    {
      label: 'Health_History.Daily_Activity_Level.Moderately_Active',
      value: DailyActivityLevel.Moderate
    },
    {
      label: 'Health_History.Daily_Activity_Level.Very_Active',
      value: DailyActivityLevel.Very
    },
    {
      label: 'Health_History.Daily_Activity_Level.Intense_Activity',
      value: DailyActivityLevel.Intense
    }
  ]
};

export const satisfiedWithWeight: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.SatisfiedWithWeight,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Satisfied_With_Weight.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const ethnicity: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.Ethnicity,
  type: HealthHistoryQuestionType.Select,
  label: 'Health_History.Ethnicity.Label',
  placeholder: 'Health_History.Ethnicity.Select_Ethnicity',
  options: [
    {
      label: 'Health_History.Ethnicity.American_Indian_Or_Alaska_Native',
      value: Ethnicity.AmericanIndianOrAlaskaNative
    },
    {
      label: 'Health_History.Ethnicity.Asian',
      value: Ethnicity.Asian
    },
    {
      label: 'Health_History.Ethnicity.Black_Or_African_American',
      value: Ethnicity.BlackOrAfricanAmerican
    },
    {
      label: 'Health_History.Ethnicity.Hispanic_Or_Latino',
      value: Ethnicity.HispanicOrLatino
    },
    {
      label: 'Health_History.Ethnicity.Native_Hawaiian_Or_Other_Pacific_Islander',
      value: Ethnicity.NativeHawaiianOrOtherPacificIslander
    },
    {
      label: 'Health_History.Ethnicity.White',
      value: Ethnicity.White
    }
  ]
};

export const feltEnergyLevel: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltEnergyLevel,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Energy_Level.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Energy_Level.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Energy_Level.High_Value_Label'
};

export const feltPain: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltPain,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Pain.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Pain.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Pain.High_Value_Label'
};

export const feltHappiness: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltHappiness,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Happiness.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Happiness.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Happiness.High_Value_Label'
};

export const feltThinking: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltThinking,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Thinking.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Thinking.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Thinking.High_Value_Label'
};

export const feltContentment: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltContentment,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Contentment.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Contentment.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Contentment.High_Value_Label'
};

export const feltMemory: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltMemory,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Memory.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Memory.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Memory.High_Value_Label'
};

export const feltEating: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltEating,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Eating.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Eating.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Eating.High_Value_Label'
};

export const feltSleep: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltSleep,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Sleep.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Sleep.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Sleep.High_Value_Label'
};

export const feltBodyShape: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltBodyShape,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Body_Shape.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Body_Shape.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Body_Shape.High_Value_Label'
};

export const feltTummy: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltTummy,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Tummy.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Tummy.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Tummy.High_Value_Label'
};

export const feltBowelMovement: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltBowelMovement,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Bowel_Movement.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Bowel_Movement.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Bowel_Movement.High_Value_Label'
};

export const feltPurpose: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltPurpose,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Purpose.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Purpose.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Purpose.High_Value_Label'
};

export const feltSelfCare: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltSelfCare,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Self_Care.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Self_Care.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Self_Care.High_Value_Label'
};

export const feltRelationships: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltRelationships,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Relationships.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Relationships.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Relationships.High_Value_Label'
};

export const feltSpiritually: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltSpiritually,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Spiritually.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Spiritually.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Spiritually.High_Value_Label'
};

export const feltMotivation: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeltMotivation,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Felt_Motivation.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Felt_Motivation.Low_Value_Label',
  highValue: 5,
  highValueLabel: 'Health_History.Felt_Motivation.High_Value_Label'
};

export const physicalActivityTimeSpent: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.PhysicalActivityTimeSpent,
  type: HealthHistoryQuestionType.Select,
  label: 'Health_History.Physical_Activity_Time_Spent.Label',
  placeholder: 'Health_History.Physical_Activity_Time_Spent.Select_Amount_Of_Time',
  options: [
    {
      label: 'Health_History.Physical_Activity_Time_Spent.None',
      value: 0
    },
    {
      label: 'Health_History.Physical_Activity_Time_Spent.Light',
      value: 30
    },
    {
      label: 'Health_History.Physical_Activity_Time_Spent.Moderate',
      value: 120
    },
    {
      label: 'Health_History.Physical_Activity_Time_Spent.Heavy',
      value: 180
    },
    {
      label: 'Health_History.Physical_Activity_Time_Spent.Intense',
      value: 420
    }
  ]
};

export const physicalActivityType: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.PhysicalActivityType,
  type: HealthHistoryQuestionType.MultiSelect,
  label: 'Health_History.Physical_Activity_Type.Label',
  placeholder: 'Health_History.Physical_Activity_Type.Select_Activities',
  options: [
    {
      label: 'Health_History.Physical_Activity_Type.Hiking',
      value: PhysicalActivityType.Hiking
    },
    {
      label: 'Health_History.Physical_Activity_Type.Weight_Lifting',
      value: PhysicalActivityType.WeightLifting
    },
    {
      label: 'Health_History.Physical_Activity_Type.Cycling',
      value: PhysicalActivityType.Cycling
    },
    {
      label: 'Health_History.Physical_Activity_Type.Walking',
      value: PhysicalActivityType.Walking
    },
    {
      label: 'Health_History.Physical_Activity_Type.Running',
      value: PhysicalActivityType.Running
    },
    {
      label: 'Health_History.Physical_Activity_Type.Sports',
      value: PhysicalActivityType.Sports
    },
    {
      label: 'Health_History.Physical_Activity_Type.Skiing',
      value: PhysicalActivityType.Skiing
    },
    {
      label: 'Health_History.Physical_Activity_Type.Dancing',
      value: PhysicalActivityType.Dancing
    },
    {
      label: 'Health_History.Physical_Activity_Type.Swimming',
      value: PhysicalActivityType.Swimming
    },
    {
      label: 'Health_History.Physical_Activity_Type.Gymnastics',
      value: PhysicalActivityType.Gymnastics
    },
    {
      label: 'Health_History.Physical_Activity_Type.Manual_Labor',
      value: PhysicalActivityType.ManualLabor
    },
    {
      label: 'Health_History.Physical_Activity_Type.Yoga',
      value: PhysicalActivityType.Yoga
    },
    {
      label: 'Health_History.Physical_Activity_Type.Other',
      value: PhysicalActivityType.Other
    }
  ]
};

export const hoursOfSleep: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HoursOfSleep,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Hours_Of_Sleep.Label',
  lowValue: 1,
  highValue: 11
};

export const glassesOfWater: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.GlassesOfWater,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Glasses_Of_Water.Label',
  lowValue: 0,
  highValue: 11
};

export const stressWork: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.StressWork,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Stress_Work.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Low',
  highValue: 10,
  highValueLabel: 'Health_History.High'
};

export const stressFamily: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.StressFamily,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Stress_Family.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Low',
  highValue: 10,
  highValueLabel: 'Health_History.High'
};

export const stressSocial: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.StressSocial,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Stress_Social.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Low',
  highValue: 10,
  highValueLabel: 'Health_History.High'
};

export const stressFinancial: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.StressFinancial,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Stress_Financial.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Low',
  highValue: 10,
  highValueLabel: 'Health_History.High'
};

export const stressHealth: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.StressHealth,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Stress_Health.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Low',
  highValue: 10,
  highValueLabel: 'Health_History.High'
};

export const stressOther: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.StressOther,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Stress_Other.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Low',
  highValue: 10,
  highValueLabel: 'Health_History.High'
};

export const eatOut: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.EatOut,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Eat_Out.Label',
  options: [
    {
      label: 'Health_History.Eat_Out.Zero_To_One',
      value: EatOut.ZeroToOne
    },
    {
      label: 'Health_History.Eat_Out.Two_To_Three',
      value: EatOut.TwoToThree
    },
    {
      label: 'Health_History.Eat_Out.Four_To_Five',
      value: EatOut.FourToFive
    },
    {
      label: 'Health_History.Eat_Out.Six_To_Seven',
      value: EatOut.SixToSeven
    },
    {
      label: 'Health_History.Eat_Out.Eight_Plus',
      value: EatOut.EightPlus
    }
  ]
};

export const dietIsHealthy: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.DietIsHealthy,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Diet_Is_Healthy.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Disagree',
  highValue: 10,
  highValueLabel: 'Health_History.Agree'
};

export const eatBreakfast: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.EatBreakfast,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Eat_Breakfast.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Disagree',
  highValue: 10,
  highValueLabel: 'Health_History.Agree'
};

export const eatHealthySnacks: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.EatHealthySnacks,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Eat_Healthy_Snacks.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Disagree',
  highValue: 10,
  highValueLabel: 'Health_History.Agree'
};

export const neverEatLate: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.NeverEatLate,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Never_Eat_Late.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Disagree',
  highValue: 10,
  highValueLabel: 'Health_History.Agree'
};

export const adoptHealthierLifestyle: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.AdoptHealthierLifestyle,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Adopt_Healthier_Lifestyle.Label',
  lowValue: 0,
  lowValueLabel: 'Health_History.Disagree',
  highValue: 10,
  highValueLabel: 'Health_History.Agree'
};

export const stressImpactAppetite: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.StressImpactAppetite,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Stress_Impact_Appetite.Label',
  options: [
    {
      label: 'Health_History.Stress_Impact_Appetite.Increased',
      value: StressImpactAppetite.Increased
    },
    {
      label: 'Health_History.Stress_Impact_Appetite.Not_Affected',
      value: StressImpactAppetite.NotAffected
    },
    {
      label: 'Health_History.Stress_Impact_Appetite.Decreased',
      value: StressImpactAppetite.Decreased
    }
  ]
};

export const foodCravings: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FoodCravings,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Food_Cravings.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const feelEnergized: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.FeelEnergized,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Feel_Energized.Label',
  options: [
    {
      label: 'Health_History.Feel_Energized.Morning',
      value: FeelEnergized.Morning
    },
    {
      label: 'Health_History.Feel_Energized.Midday',
      value: FeelEnergized.Midday
    },
    {
      label: 'Health_History.Feel_Energized.Evening',
      value: FeelEnergized.Evening
    }
  ]
};

export const sugaryDrinks: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.SugaryDrinks,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Sugary_Drinks.Label',
  options: [
    {
      label: 'Health_History.Sugary_Drinks.Zero',
      value: SugaryDrinks.Zero
    },
    {
      label: 'Health_History.Sugary_Drinks.One_To_Two',
      value: SugaryDrinks.OneToTwo
    },
    {
      label: 'Health_History.Sugary_Drinks.Three_To_Five',
      value: SugaryDrinks.ThreeToFive
    },
    {
      label: 'Health_History.Sugary_Drinks.Six_To_Nine',
      value: SugaryDrinks.SixToNine
    },
    {
      label: 'Health_History.Sugary_Drinks.Ten_Plus',
      value: SugaryDrinks.TenPlus
    }
  ]
};

export const alcoholUse: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.AlcoholUse,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Alcohol_Use.Label',
  options: [
    {
      label: 'Health_History.Alcohol_Use.Zero',
      value: SugaryDrinks.Zero
    },
    {
      label: 'Health_History.Alcohol_Use.One_To_Two',
      value: SugaryDrinks.OneToTwo
    },
    {
      label: 'Health_History.Alcohol_Use.Three_To_Five',
      value: SugaryDrinks.ThreeToFive
    },
    {
      label: 'Health_History.Alcohol_Use.Six_To_Nine',
      value: SugaryDrinks.SixToNine
    },
    {
      label: 'Health_History.Alcohol_Use.Ten_Plus',
      value: SugaryDrinks.TenPlus
    }
  ]
};

export const tobaccoUse: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.TobaccoUse,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Tobacco_Use.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const drugUse: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.DrugUse,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Drug_Use.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const toxinConcern: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ToxinConcern,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Toxin_Concern.Label',
  options: [
    {
      label: 'Health_History.Toxin_Concern.None',
      value: ToxinConcern.None
    },
    {
      label: 'Health_History.Toxin_Concern.Somewhat',
      value: ToxinConcern.Somewhat
    },
    {
      label: 'Health_History.Toxin_Concern.Very',
      value: ToxinConcern.Very
    }
  ]
};

export const healthConcernPhysicalActivity: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernPhysicalActivity,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Physical_Activity.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernNutrition: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernNutrition,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Nutrition.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernOverweight: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernOverweight,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Overweight.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernTobacco: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernTobacco,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Tobacco.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernSubstanceAbuse: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernSubstanceAbuse,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Substance_Abuse.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernMentalHealth: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernMentalHealth,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Mental_Health.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernInjuryViolence: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernInjuryViolence,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Injury_Violence.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernEnvironmentalQuality: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernEnvironmentalQuality,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Environmental_Quality.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernImmunization: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernImmunization,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Immunization.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const healthConcernAccessToHealthcare: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HealthConcernAccessToHealthcare,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Health_Concern_Access_To_Healthcare.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.None',
  highValue: 5,
  highValueLabel: 'Health_History.Most'
};

export const howOftenSick: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.HowOftenSick,
  type: HealthHistoryQuestionType.Select,
  label: 'Health_History.How_Often_Sick.Label',
  placeholder: 'Health_History.How_Often_Sick.Select_Frequency',
  options: [
    {
      label: 'Health_History.How_Often_Sick.Once_Per_Month',
      value: HowOftenSick.OncePerMonth
    },
    {
      label: 'Health_History.How_Often_Sick.Once_Per_Three_Months',
      value: HowOftenSick.OnceThreeMonths
    },
    {
      label: 'Health_History.How_Often_Sick.Once_Per_Six_Months',
      value: HowOftenSick.OnceSixMonths
    },
    {
      label: 'Health_History.How_Often_Sick.Once_Per_Year',
      value: HowOftenSick.OnceYear
    },
    {
      label: 'Health_History.How_Often_Sick.Rarely',
      value: HowOftenSick.Rarely
    }
  ]
};

export const enoughEnergyForDailyActivities: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.EnoughEnergyForDailyActivities,
  type: HealthHistoryQuestionType.Segment,
  label: 'Health_History.Enough_Energy_For_Daily_Activities.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const motivationForGoals: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.MotivationForGoals,
  type: HealthHistoryQuestionType.Slider,
  label: 'Health_History.Motivation_For_Goals.Label',
  lowValue: 1,
  lowValueLabel: 'Health_History.Not',
  highValue: 10,
  highValueLabel: 'Health_History.Very'
};

export const dietaryGoals: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.DietaryGoals,
  type: HealthHistoryQuestionType.MultiSelect,
  label: 'Health_History.Dietary_Goals.Label',
  placeholder: 'Health_History.Dietary_Goals.Select_Dietary_Goals',
  options: [
    {
      label: 'Health_History.Dietary_Goals.Healthy_Eating_Optimal_Nutrients',
      value: DietaryGoals.HealthyEatingOptimalNutrients
    },
    {
      label: 'Health_History.Dietary_Goals.Weight_Loss',
      value: DietaryGoals.WeightLoss
    },
    {
      label: 'Health_History.Dietary_Goals.Limit_Sugar_Sodium',
      value: DietaryGoals.LimitSugarSodium
    },
    {
      label: 'Health_History.Dietary_Goals.Healthier_Food_More_Water',
      value: DietaryGoals.HealthierFoodMoreWater
    }
  ]
};

export const conditionAutoImmune: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionAutoImmune,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Auto_Immune.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionBowelOstomy: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionBowelOstomy,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Bowel_Ostomy.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};
export const conditionCancer: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionCancer,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Cancer.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionCeliac: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionCeliac,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Celiac.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionCopd: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionCopd,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Copd.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionChf: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionChf,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Chf.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionConstipation: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionConstipation,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Constipation.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionCoronaryArtery: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionCoronaryArtery,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Coronary_Artery.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionCrohns: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionCrohns,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Crohns.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionCysticFibrosis: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionCysticFibrosis,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Cystic_Fibrosis.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionDimentiaAlzeimerParkinson: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionDimentiaAlzeimerParkinson,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Dimentia_Alzeimer_Parkinson.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionDiabetes: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionDiabetes,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Diabetes.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionDiarrhea: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionDiarrhea,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Diarrhea.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionDiverticulitis: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionDiverticulitis,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Diverticulitis.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionEatingDisorder: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionEatingDisorder,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Eating_Disorder.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionGerd: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionGerd,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Gerd.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionGastroparesis: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionGastroparesis,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Gastroparesis.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionGout: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionGout,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Gout.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionHighBloodPressure: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionHighBloodPressure,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_High_Blood_Pressure.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionHighCholesterol: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionHighCholesterol,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_High_Cholesterol.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionHivImmuneSuppression: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionHivImmuneSuppression,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Hiv_Immune_Suppression.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionIbs: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionIbs,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Ibs.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionKidneyDisease: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionKidneyDisease,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Kidney_Disease.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionOrganTransplant: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionOrganTransplant,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Organ_Transplant.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionOsteoporosis: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionOsteoporosis,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Osteoporosis.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionOverweightObesity: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionOverweightObesity,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Overweight_Obesity.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionPancreatitis: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionPancreatitis,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Pancreatitis.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionPepticUlcer: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionPepticUlcer,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Peptic_Ulcer.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionTroubleChewingSwallowing: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionTroubleChewingSwallowing,
  type: HealthHistoryQuestionType.Radio,
  label: 'Health_History.Condition_Trouble_Chewing_Swallowing.Label',
  options: [
    {
      label: 'Health_History.No',
      value: Condition.No
    },
    {
      label: 'Health_History.Had',
      value: Condition.Had
    },
    {
      label: 'Health_History.Have',
      value: Condition.Have
    }
  ]
};

export const conditionOther: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionOther,
  type: HealthHistoryQuestionType.Checkbox,
  label: 'Health_History.Condition_Other.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const conditionNone: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.ConditionNone,
  type: HealthHistoryQuestionType.Checkbox,
  label: 'Health_History.Condition_None.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const prescribedDiet: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.PrescribedDiet,
  type: HealthHistoryQuestionType.Checkbox,
  label: 'Health_History.Prescribed_Diet.Label',
  options: [
    {
      label: 'Common.No',
      value: false
    },
    {
      label: 'Common.Yes',
      value: true
    }
  ]
};

export const prescribedDietDescription: HealthHistoryQuestion = {
  key: HealthHistoryQuestionnaireKey.PrescribedDietDescription,
  type: HealthHistoryQuestionType.String,
  label: 'Health_History.Prescribed_Diet_Description.Label',
  placeholder: 'Health_History.Prescribed_Diet_Description.Please_Describe',
  triggeredBy: HealthHistoryQuestionnaireKey.PrescribedDiet,
  triggeredByValues: [true]
};

export const healthHistoryQuestions: HealthHistoryQuestion[] = [
  birthday,
  geneticSex,
  pregnantLactating,
  height,
  weight,
  waistCircumference,
  dailyActivityLevel,
  satisfiedWithWeight,
  ethnicity,
  feltEnergyLevel,
  feltPain,
  feltHappiness,
  feltThinking,
  feltContentment,
  feltMemory,
  feltEating,
  feltSleep,
  feltBodyShape,
  feltTummy,
  feltBowelMovement,
  feltPurpose,
  feltSelfCare,
  feltRelationships,
  feltSpiritually,
  feltMotivation,
  physicalActivityTimeSpent,
  physicalActivityType,
  hoursOfSleep,
  glassesOfWater,
  stressWork,
  stressFamily,
  stressSocial,
  stressFinancial,
  stressHealth,
  stressOther,
  eatOut,
  dietIsHealthy,
  eatBreakfast,
  eatHealthySnacks,
  neverEatLate,
  adoptHealthierLifestyle,
  stressImpactAppetite,
  foodCravings,
  feelEnergized,
  sugaryDrinks,
  alcoholUse,
  tobaccoUse,
  drugUse,
  toxinConcern,
  healthConcernPhysicalActivity,
  healthConcernNutrition,
  healthConcernOverweight,
  healthConcernTobacco,
  healthConcernSubstanceAbuse,
  healthConcernMentalHealth,
  healthConcernInjuryViolence,
  healthConcernEnvironmentalQuality,
  healthConcernImmunization,
  healthConcernAccessToHealthcare,
  howOftenSick,
  motivationForGoals,
  dietaryGoals,
  conditionAutoImmune,
  conditionBowelOstomy,
  conditionCancer,
  conditionCeliac,
  conditionChf,
  conditionConstipation,
  conditionCopd,
  conditionCoronaryArtery,
  conditionCrohns,
  conditionCysticFibrosis,
  conditionDiabetes,
  conditionDiarrhea,
  conditionDimentiaAlzeimerParkinson,
  conditionDiverticulitis,
  conditionEatingDisorder,
  conditionGastroparesis,
  conditionGerd,
  conditionGout,
  conditionHighBloodPressure,
  conditionHighCholesterol,
  conditionHivImmuneSuppression,
  conditionIbs,
  conditionKidneyDisease,
  conditionOrganTransplant,
  conditionOsteoporosis,
  conditionOverweightObesity,
  conditionPancreatitis,
  conditionPepticUlcer,
  conditionTroubleChewingSwallowing,
  conditionOther,
  conditionNone,
  prescribedDiet,
  prescribedDietDescription
];
